import type { Player } from '@goodtime/shared';
import { getSecure, setSecure } from '../storage/secureStore';
import { api, ApiError, playerToken, setPlayerToken } from './client';

export function createAnonymousPlayer(): Promise<Player> {
  return api<Player>('/players/anonymous', { method: 'POST' });
}

export function fetchPlayer(id: string): Promise<Player> {
  return api<Player>(`/players/${encodeURIComponent(id)}`);
}

/** Key the stable anonymous player id lives under in {@link secureStore}. */
const PLAYER_ID_KEY = 'goodtime.playerId';

/**
 * Resolve the STABLE anonymous player — the foundation of data persistence.
 *
 * <p>Reuses the id persisted from a prior launch (via {@link getSecure})
 * instead of minting a throwaway player every boot. Without this, every
 * launch created a brand-new server player and orphaned the previous one's
 * wallet + profile backup. Mints + persists a fresh id only on the first
 * ever launch — or if the stored id no longer exists server-side (e.g. a
 * dev DB reset → 404).
 *
 * <p>Reinstall recovery depends on the {@link secureStore} backend surviving
 * uninstall (Keychain). Until that swap lands the id survives relaunches but
 * not a delete + reinstall — see {@code secureStore.ts}.
 */
export function getOrCreatePlayer(): Promise<Player> {
  // One at a time. React StrictMode runs the boot effect twice in dev; two
  // calls in flight together both found no stored id, both minted, and the
  // stored id could end up naming the player the app wasn't using.
  inFlight ??= fetchOrMintPlayer().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

let inFlight: Promise<Player> | null = null;

async function fetchOrMintPlayer(): Promise<Player> {
  const storedId = await getSecure(PLAYER_ID_KEY);
  if (storedId) {
    try {
      const player = await fetchPlayer(storedId);
      await claimTokenIfMissing(player.id);
      return player;
    } catch (e) {
      // Hard 404 → the server no longer has this player (dev DB reset); fall
      // through to mint a fresh one. Any other error (network / 5xx) is
      // transient — rethrow so the caller surfaces a connection error rather
      // than silently abandoning the stored id and splitting the account.
      if (!(e instanceof ApiError) || e.status !== 404) throw e;
    }
  }
  const player = await createAnonymousPlayer();
  await setSecure(PLAYER_ID_KEY, player.id);
  if (player.token) await setPlayerToken(player.token);
  return player;
}

/** A player from before tokens claims one, once. A 409 means the player
 *  already has a token (on another device, or claimed by someone else);
 *  account recovery is then the way back in. */
async function claimTokenIfMissing(playerId: string): Promise<void> {
  try {
    if (await playerToken()) return;
    const { token } = await api<{ token: string }>(`/players/${encodeURIComponent(playerId)}/token`, {
      method: 'POST',
    });
    await setPlayerToken(token);
  } catch (e) {
    console.warn('[players] could not claim a player token', e);
  }
}

/** Adopt a recovered player id (account recovery): persist it as THIS device's
 *  player. On the next boot {@link getOrCreatePlayer} reads it and profile-sync
 *  restores that player's progress. Reload after calling. */
export async function adoptPlayerId(id: string, token?: string | null): Promise<void> {
  await setSecure(PLAYER_ID_KEY, id);
  if (token) await setPlayerToken(token);
}

/** Outcome of a display-name change. {@code accepted} → the name was applied;
 *  otherwise {@code reason} is a canned, player-facing message (the name was
 *  blocked by the abuse check — deterministic guard or AI review). */
export interface RenameResult {
  accepted: boolean;
  name: string | null;
  reason: string | null;
}

/**
 * Set the player's display name. The server runs an abuse check (length/charset
 * guards + AI review) and returns {@link RenameResult}; a blocked name comes
 * back with {@code accepted=false} + a canned reason rather than an error.
 */
export function renameDisplayName(playerId: string, name: string): Promise<RenameResult> {
  return api<RenameResult>(`/players/${encodeURIComponent(playerId)}/name`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

/**
 * Advance the onboarding-session pointer (1..5). Server-side service
 * is monotonic + clamped so it's safe to call with any target;
 * requests at-or-below the current step are no-ops. See UX §4.
 */
export function advanceOnboardingSession(
  playerId: string,
  target: number,
): Promise<Player> {
  return api<Player>(`/players/${encodeURIComponent(playerId)}/onboarding-session`, {
    method: 'PUT',
    body: JSON.stringify({ target }),
  });
}
